/**
 * Utility functions for fetching and handling project data
 */

/**
 * Fetches all project data from the public/projects directory
 * @returns {Promise<Array>} Array of project objects
 */
export const fetchProjects = async () => {
  try {
    // Fetch the manifest file which contains an array of project filenames
    const response = await fetch('/projects/manifest.json');
    
    if (!response.ok) {
      throw new Error('Failed to load project manifest');
    }
    
    const manifest = await response.json();
    const projectFilenames = manifest.projects || [];
    
    // Map each filename to a project object by fetching its content
    const projectPromises = projectFilenames.map(async (filename) => {
      const projectResponse = await fetch(`/projects/${filename}`);
      if (!projectResponse.ok) {
        console.error(`Failed to load project: ${filename}`);
        return null;
      }
      const projectData = await projectResponse.json();
      
      // Add an id based on filename if not provided
      return {
        id: filename.replace('.json', ''),
        ...projectData
      };
    });
    
    // Wait for all projects to load and filter out any that failed
    const projects = (await Promise.all(projectPromises)).filter(project => project !== null);
    
    // Sort projects by date in descending order or by featured status
    return projects.sort((a, b) => {
      // First sort by featured status
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      
      // Then by date if available
      const dateA = a.dateCompleted ? new Date(a.dateCompleted) : new Date(0);
      const dateB = b.dateCompleted ? new Date(b.dateCompleted) : new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};
